/* eslint-disable react/prop-types */
import { ApiClient } from '@api/api-configuration';
import { ServiceDb } from '@api/READ_ONLY/caregiver_api/Api';
import { ServicePopulated } from '@api/READ_ONLY/services_api/Api';
import { captureError } from '@app/lib/Sentry';
import ServicesSelectGeneric from '@feat-services/components/ServicesSelect/ServicesSelectGeneric';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomComponentInputs } from '../filters.types';

/**
 * Services select wrapper for query filter system
 * Handles both single and multiple selection modes
 * Fetches service objects when IDs are provided from URL
 */
export const ServiceFilterSelect: React.FC<CustomComponentInputs> = ({ onChange, value }) => {
  const { t } = useTranslation();
  const [loadedServices, setLoadedServices] = useState<ServicePopulated[]>([]);

  // Normalize value to always be an array for mode="multiple"
  const normalizedValue = useMemo(() => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value.map((v) => Number(v));
    }
    return [Number(value)];
  }, [value]);

  // Fetch services by IDs when value is provided (e.g., from URL)
  useEffect(() => {
    const fetchServicesByIds = async (ids: number[]) => {
      try {
        const services = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await ApiClient.serviceApi.services.getServiceById(id);
              return response.data as ServicePopulated;
            } catch (error) {
              captureError(error instanceof Error ? error : new Error(`Error fetching service ID ${id}`), {
                serviceId: id,
                component: 'ServiceFilterSelect',
              });
              return null;
            }
          })
        );
        const filtered = services.filter((s) => s !== null) as ServicePopulated[];
        setLoadedServices(filtered);
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('Error fetching services'), {
          serviceIds: ids,
          component: 'ServiceFilterSelect',
        });
      }
    };

    if (normalizedValue && normalizedValue.length > 0) {
      fetchServicesByIds(normalizedValue);
    } else {
      setLoadedServices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedValue]);

  const handleChange = (service: ServiceDb | ServiceDb[] | undefined) => {
    if (Array.isArray(service)) {
      // Multiple selection - return array of IDs
      onChange(service.map((s) => s.serviceId));
    } else if (service) {
      // Single selection - return single ID
      onChange(service.serviceId);
    } else {
      // Cleared
      onChange(null);
    }
  };

  return (
    <ServicesSelectGeneric
      allowClear={true}
      mode="multiple"
      placeholder={t('filters-select-services-placeholder')}
      maxTagCount="responsive"
      onServiceChange={handleChange}
      value={normalizedValue}
      selectedItemsProvider={() => (loadedServices.length > 0 ? loadedServices : undefined)}
      style={{ width: '100%', height: '44px' }}
      className="h-8"
    />
  );
};
